<template>
  <div class="linexo details">
    <div class="details__header">
      <AHeadline text="details.headline" type="h3" />
      <LButton variant="tertiary" prepend-icon="lnx-close" @click="onDetailButtonClose">
        {{ $t('details.close') }}
      </LButton>
    </div>

    <LTable class="details__table">
      <LTHead>
        <LTR>
          <LTH></LTH>
          <LTH align="right">{{ $t('details.private_buy') }}</LTH>
          <LTH align="right">{{ $t('details.company_bike') }}</LTH>
        </LTR>
      </LTHead>

      <LTBody>
        <LTR>
          <LTH>{{ $t('details.gross_salary') }}</LTH>
          <LTD align="right">{{ getDisplayedPrice(privateBuy.grossSalary) }}</LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.grossSalary) }}</LTD>
        </LTR>

        <LTR variant="subtle">
          <LTH>{{ $t('details.employer_subsidy') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.employerSubsidy, { showNull: true, forcePrefix: true }) }}</LTD>
        </LTR>

        <LTR>
          <LTH>{{ $t('details.leasing_rate') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.leasingRate, { negate: true, showNull: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle">
          <LTH>{{ $t('details.insurance_premium') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.insuranceBonus, { negate: true, showNull: true }) }}</LTD>
        </LTR>

        <LTR>
          <LTH>{{ $t('details.monetary_advantage') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.monetaryAdvantage, { showNull: true, forcePrefix: true }) }}</LTD>
        </LTR>

        <LTR variant="highlight" text-style="highlight">
          <LTH>{{ $t('details.calculation_basis') }}</LTH>
          <LTD align="right">{{ getDisplayedPrice(privateBuy.calculationBasis, { showNull: true }) }}</LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.calculationBasis, { showNull: true }) }}</LTD>
        </LTR>

        <LTR>
          <LTH>{{ $t('details.tax_deductions') }}</LTH>
          <LTD align="right">{{ getDisplayedPrice(privateBuy.tax, { showNull: true, negate: true }) }}</LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.tax, { showNull: true, negate: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle">
          <LTH>{{ $t('details.social_insurance_deductions') }}</LTH>
          <LTD align="right">{{ getDisplayedPrice(privateBuy.socialInsurance, { showNull: true, negate: true }) }}</LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.socialInsurance, { showNull: true, negate: true }) }}</LTD>
        </LTR>

        <LTR>
          <LTH>{{ $t('details.monetary_advantage') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ getDisplayedPrice(leasing.monetaryAdvantage, { showNull: true, negate: true }) }}</LTD>
        </LTR>

        <LTR text-style="highlight">
          <LTH>{{ $t('details.net_payout') }}</LTH>
          <LTD align="right">{{ privateBuy.netPayout ? formatPrice(privateBuy.netPayout) : '' }}</LTD>
          <LTD align="right">{{ leasing.netPayout ? formatPrice(leasing.netPayout) : '' }}</LTD>
        </LTR>
      </LTBody>

      <LTFoot>
        <LTR>
          <LTH colspan="3" align="center">
            <template #overline>
              <span class="details__footer-overline">{{ $t('details.monthly_net_burden') }}</span>
            </template>
            <span class="details__footer-price">{{ formatPrice(price) }}</span>
          </LTH>
        </LTR>
      </LTFoot>
    </LTable>

    <LTable class="details__table-bottom">
      <LTHead>
        <LTR>
          <LTH></LTH>
          <LTH align="right">{{ $t('details.private_buy') }}</LTH>
          <LTH align="right">{{ $t('details.company_bike') }}</LTH>
        </LTR>
      </LTHead>
      <LTBody>
        <LTR>
          <LTH variant="subtle">{{ $t('details.net_burden_duration') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ leasing.netBurdenDuration ? formatPrice(leasing.netBurdenDuration) : '' }}</LTD>
        </LTR>
        <LTR variant="subtle">
          <LTH variant="subtle">{{ $t('details.acquisition_price') }}</LTH>
          <LTD align="right">{{ privateBuy.purchasePrice ? formatPrice(privateBuy.purchasePrice) : '' }}</LTD>
          <LTD align="right">{{ $t('details.included') }}</LTD>
        </LTR>
        <LTR>
          <LTH variant="subtle">{{ $t('details.insurance_cost') }}</LTH>
          <LTD align="right">{{ privateBuy.insuranceCost ? formatPrice(privateBuy.insuranceCost) : '' }}</LTD>
          <LTD align="right">{{ $t('details.included') }}</LTD>
        </LTR>
        <LTR variant="subtle">
          <LTH variant="subtle">{{ $t('details.expected_used_purchase_price') }}</LTH>
          <LTD align="right"></LTD>
          <LTD align="right">{{ leasing.expectedUsedPurchasePrice ? formatPrice(leasing.expectedUsedPurchasePrice) : '' }}</LTD>
        </LTR>
        <LTR variant="highlight" text-style="highlight">
          <LTH>{{ $t('details.total_cost') }}</LTH>
          <LTD align="right">{{ privateBuy.totalCost ? formatPrice(privateBuy.totalCost) : '' }}</LTD>
          <LTD align="right">{{ leasing.totalCost ? formatPrice(leasing.totalCost) : '' }}</LTD>
        </LTR>
      </LTBody>

      <LTFoot>
        <LTR>
          <LTH colspan="3" align="center">
            <template #overline>
              <span class="details__footer-overline">{{ $t('details.total_savings') }}</span>
            </template>
            <span class="details__footer-price">
              {{ formatPrice(savings) }}
              <span class="details__footer-percentage"> ({{ formatPercentage(savingsPercentage) }})</span>
            </span>
          </LTH>
        </LTR>
      </LTFoot>
    </LTable>
  </div>
</template>

<script lang="ts" setup>
import AHeadline from '@/components/atoms/content/headline/A-Headline.vue'
import { LButton, LTable, LTHead, LTBody, LTFoot, LTR, LTH, LTD } from '@wgg.hyve/hyve-ui-library'
import { onMounted } from 'vue'
import { formatPercentage, formatPrice, getDisplayedPrice, scrollToElement } from '@/services/helper.service.ts'

type Props = {
  price: number
  savings: number
  savingsPercentage: number
  showDetailsTable: boolean
  privateBuy: {
    grossSalary: number | null
    calculationBasis: number | null
    tax: number | null
    socialInsurance: number | null
    netPayout: number | null
    purchasePrice: number | null
    insuranceCost: number | null
    totalCost: number | null
  }
  leasing: {
    grossSalary: number | null
    employerSubsidy: number | null
    leasingRate: number | null
    insuranceBonus: number | null
    monetaryAdvantage: number | null
    calculationBasis: number | null
    tax: number | null
    socialInsurance: number | null
    netPayout: number | null
    netBurdenDuration: number | null
    expectedUsedPurchasePrice: number | null
    totalCost: number | null
  }
}

const props = defineProps<Props>()

const emit = defineEmits(['update:showDetailsTable'])

onMounted(() => {
  scrollToElement('.details__header', 48)
})

async function onDetailButtonClose() {
  await scrollToElement('#lnx-calculator', 48)
  emit('update:showDetailsTable', false)
}
</script>

<style lang="scss">
@import 'm-detail-table.scss';
</style>
